import React, { useEffect, useMemo, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import huLocale from '@fullcalendar/core/locales/hu';
import dayjs from 'dayjs';
import CalendarFilter from './CalendarFilter';
import useUsers from '../../context/UsersContext';
import masterDataService from '../../service/masterData.service';
import colorFinder from '../../utils/colorFinder';
import useConfig from '../../context/ConfigContext';
import taskCalendarService from '../../service/taskCalendar.service';
import taskListService from '../../service/taskList.service';
import ProjectTaskDialog from '../ProjectTasks/ProjectTaskDialog';
import useView from '../../hooks/useView';
import SimpleTaskEventContent from './SimpleTaskEventContent';
import { CalendarType } from '../../config/taskCalendar';
import TaskListEventContent from './TaskListEventContent';
import simpletaskService from '../../service/simpletask.service';
import SimpleTaskDialog from './SimpleTaskDialog';
import SelectDialog from './SelectDialog';
import { useMediaQuery } from '@mui/material';
import EmployeeAttendanceDialog from '../EmployeeAttendance/EmployeeAttendanceDialog';
import useParam from '../../context/ParamContext';
import { generateDefaultTime } from '../../utils/helper';

export const TaskCalendar = () => {
  const { user, getUser, getRole } = useUsers();
  const { config, getConfig } = useConfig();
  const { params, getParam } = useParam();
  const calendarRef = useRef(null);
  const savedFilters = useView('taskCalendarFilter', user);
  const [events, setEvents] = useState([]);
  const [dateRange, setDateRange] = useState({});
  const [query, setQuery] = useState({
    filter: { filterModel: { items: [], logicOperator: 'and' } },
  });
  const [resources, setResources] = useState({
    user: [],
    project: [],
    employee: [],
  });
  const [date, setDate] = useState({
    startDate: null,
    endDate: null,
  });
  const [taskListDialog, setTaskListDialog] = useState({ open: false, data: {} });
  const [simpleTaskDialog, setSimpleTaskDialog] = useState({ open: false, data: {} });
  const [taskChanged, setTaskChanged] = useState(false);
  const [headerOffset, setHeaderOffset] = useState(0);
  const [roles, setRoles] = useState({});
  const [calendarType, setCalendarType] = useState([]);
  const [selectDialogData, setSelectDialogData] = useState({ open: false, data: {} });
  const defSimpleTaskReportDialog = {
    open: false,
    data: {},
  };
  const [showSimpleTaskReportDialog, setShowSimpleTaskReportDialog] = useState(defSimpleTaskReportDialog);

  const getFilterItems = () => query?.filter?.filterModel?.items ?? [];

  const isLgUp = useMediaQuery('(min-width:1060px)');

  const hasResourceFilter = useMemo(() => {
    if (calendarType.length === 0) return;

    const items = getFilterItems();

    return items.some((item) => {
      const isResourceField = item.field === 'responsiblePersonId' || item.field === 'employeeId';

      const values = Array.isArray(item.value) ? item.value : [];
      return isResourceField && values.length > 0;
    });
  }, [query, calendarType]);

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    if (!Object.keys(params).length) getParam();
    initDefaultValues();
    getConfig();
  }, []);

  useEffect(() => {
    if (Object.keys(user).length > 0) {
      const roles = getRole('TASK_CALENDAR_SEE_ONLY_OWN');

      setRoles(roles);
    }
  }, [user]);

  useEffect(() => {
    setCalendarType(savedFilters?.calendarTypes || []);
  }, [savedFilters?.calendarTypes]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const update = () => setHeaderOffset(mq.matches ? 64 : 0);
    update();
    if (mq.addEventListener) {
      mq.addEventListener('change', update);
      return () => mq.removeEventListener('change', update);
    } else {
      mq.addListener(update);
      return () => mq.removeListener(update);
    }
  }, []);

  useEffect(() => {
    if (date.startDate !== null && date.endDate !== null && calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();

      if (calendarApi.view.type !== 'dayGridDay') {
        calendarApi.changeView('dayGridDay', date.startDate.toDate());
      } else {
        calendarApi.gotoDate(date.startDate.toDate());
      }
    }
  }, [date.startDate, date.endDate]);

  useEffect(() => {
    if (date.startDate !== null && date.endDate !== null) {
      setDateRange(date);
    }
  }, [date.startDate, date.endDate]);

  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate && hasResourceFilter) {
      getAllCalendarDays();
    } else {
      setEvents([]);
    }
  }, [dateRange.startDate, dateRange.endDate, hasResourceFilter, query, calendarType]);

  useEffect(() => {
    if (taskChanged && hasResourceFilter) getAllCalendarDays();
  }, [taskChanged]);

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .usersToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .employeesToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const resources = {
          user: resp[0],
          project: resp[1],
          employee: resp[2],
        };

        setResources(resources);
      })
      .finally(() => {
        //hideLoader();
      });
  };

  const calendarViews = {
    dayGridMonth: {
      dayHeaderFormat: { weekday: 'long' },
    },
    dayGridWeek: {
      dayHeaderFormat: { month: 'long', day: 'numeric', weekday: 'long' },
      slotLabelFormat: [{ hour: 'numeric', minute: '2-digit' }],
    },
    dayGridDay: {
      slotLabelFormat: [{ hour: 'numeric', minute: '2-digit' }],
    },
  };

  const getAllCalendarDays = () => {
    const reqData = {
      query,
      dateRange,
      calendarType,
    };
    taskCalendarService.getCalendarDays(reqData).then((data) => {
      const coloredData = data.map((event) => {
        const status = event?.status?.color;
        const color = colorFinder(status);
        return {
          ...event,
          backgroundColor: color.colorBgCode,
        };
      });
      setEvents(coloredData);
      setTaskChanged(false);
    });
  };

  const handleDatesSet = (dateInfo) => {
    const startDate = dayjs(dateInfo.start).startOf('day');
    const endDate = dayjs(dateInfo.end).endOf('day');

    if (dateRange.startDate?.isSame(startDate, 'day') && dateRange.endDate?.isSame(endDate, 'day')) {
      return;
    }
    setDateRange({ startDate, endDate });
  };

  const getTaskListById = (id) => {
    setTaskListDialog((prev) => ({
      ...prev,
      open: true,
    }));

    taskListService.getTasklistById(id).then((data) => {
      setTaskListDialog((prev) => ({
        ...prev,
        data: data[0],
      }));
    });
  };

  const getSimpleTaskById = (id) => {
    setSimpleTaskDialog((prev) => ({
      ...prev,
      open: true,
    }));

    simpletaskService.getTaskById(id).then((data) => {
      setSimpleTaskDialog((prev) => ({
        ...prev,
        data: data,
      }));
    });
  };

  const handleSelectDate = (selectInfo) => {
    if (roles.TASK_CALENDAR_SEE_ONLY_OWN) return;
    const { start, allDay } = selectInfo;

    let startDate = dayjs(start);

    if (allDay) {
      startDate = config.defTaskFromTime
        ? generateDefaultTime(startDate, config.defTaskFromTime)
        : startDate.startOf('day');
    }

    const duration = config?.defTaskDuration ?? 1;

    const data = {
      startDate,
      endDate: dayjs(startDate).add(duration, 'hour'),
      duration,
    };

    if (calendarType.includes(CalendarType.SIMPLETASK) && calendarType.includes(CalendarType.TASKLIST)) {
      setSelectDialogData({
        open: true,
        data: data,
      });
    } else if (calendarType.includes(CalendarType.SIMPLETASK)) {
      setSimpleTaskDialog({
        open: true,
        data: data,
      });
    } else if (calendarType.includes(CalendarType.TASKLIST)) {
      setTaskListDialog({
        open: true,
        data: data,
      });
    }
  };

  const handleSelectTaskType = (type) => {
    if (type === CalendarType.SIMPLETASK) {
      setSimpleTaskDialog({
        open: true,
        data: selectDialogData.data,
      });
    } else if (type === CalendarType.TASKLIST) {
      setTaskListDialog({
        open: true,
        data: selectDialogData.data,
      });
    }

    setSelectDialogData({ open: false, data: {} });
  };

  return (
    <div className={`taskListCalendar calendarView flex ${isLgUp ? `flex-row` : 'flex-col'} bg-[#f3f4f6]`}>
      <div
        className={`overflow-y-scroll overflow-x-hidden custom-scrollbar ${isLgUp ? 'w-[380px]' : 'max-h-[250px]'}`}
        style={{ height: isLgUp ? `calc(100dvh - ${headerOffset + 20}px)` : 'auto' }}
      >
        <CalendarFilter
          dates={{ date, setDate }}
          resources={resources}
          queryState={{ query, setQuery }}
          hasResourceFilter={hasResourceFilter}
          roles={roles}
          calendarTypeState={{ setCalendarType, calendarType }}
          isLgUp={isLgUp}
        />
      </div>
      <div className="w-full bg-white">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView={isLgUp ? 'dayGridWeek' : 'dayGridDay'}
          headerToolbar={{
            left: 'prev,next,today',
            center: 'title',
            right: 'dayGridDay,dayGridWeek,dayGridMonth',
          }}
          locales={[huLocale]}
          views={calendarViews}
          datesSet={handleDatesSet}
          nowIndicator={true}
          dayMaxEventRows={true}
          events={events}
          selectable={true}
          select={handleSelectDate}
          eventClick={(eventInfo) => {
            const { event } = eventInfo;
            const type = event.extendedProps.type;
            const id = parseInt(event.id.substring(1));

            if (type === CalendarType.SIMPLETASK) {
              getSimpleTaskById(id);
            } else if (type === CalendarType.TASKLIST) {
              getTaskListById(id);
            }
          }}
          timeZone="UTC"
          eventContent={(eventInfo) => {
            const type = eventInfo.event.extendedProps.type;

            if (type === CalendarType.SIMPLETASK) {
              return (
                <SimpleTaskEventContent
                  eventInfo={eventInfo}
                  resources={resources}
                  setShowSimpleTaskReportDialog={setShowSimpleTaskReportDialog}
                />
              );
            } else if (type === CalendarType.TASKLIST) {
              return <TaskListEventContent eventInfo={eventInfo} resources={resources} />;
            }
          }}
        />
      </div>

      <ProjectTaskDialog
        open={taskListDialog.open}
        handleClose={() =>
          setTaskListDialog({
            open: false,
            data: {},
          })
        }
        data={taskListDialog.data}
        setTaskChanged={setTaskChanged}
        disabledField={roles.TASK_CALENDAR_SEE_ONLY_OWN}
      />

      {showSimpleTaskReportDialog.open && (
        <EmployeeAttendanceDialog
          dialogData={showSimpleTaskReportDialog}
          onClose={() => setShowSimpleTaskReportDialog(defSimpleTaskReportDialog)}
        />
      )}

      <SelectDialog
        open={selectDialogData.open}
        handleClose={() => setSelectDialogData(false)}
        handleSelectTaskType={handleSelectTaskType}
      />

      <SimpleTaskDialog
        open={simpleTaskDialog.open}
        handleClose={() =>
          setSimpleTaskDialog({
            open: false,
            data: {},
          })
        }
        data={simpleTaskDialog.data}
        changed={{ setTaskChanged }}
      />
    </div>
  );
};
